import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import type { ViewStyle } from 'react-native';
import { SvgXml } from 'react-native-svg';
import NavBar from 'src/presentation/components/organisms/NavBar';
import Card from 'src/presentation/components/atoms/Card';
import Loader from 'src/presentation/components/atoms/Loader';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useMySubmission } from 'src/presentation/hooks/useMySubmission';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';

function MySubmissionScreen() {
	const eventStore = useEventStore();
	const theme = eventStore.activeTheme;
	const { status, submission } = useMySubmission(eventStore.activeEventUuid);
	const contentWidth = useContentWidth();

	// Cap the QR so it stays a comfortable, scannable square on tablets/large phones.
	const qrSize = Math.min(contentWidth - Dimens.padding40 * 2, 280);
	const sizedStyles = useMemo((): { qrBox: ViewStyle } => ({ qrBox: { width: qrSize, height: qrSize } }), [qrSize]);

	const renderContent = () => {
		if (status === 'idle' || status === 'loading') {
			return <Loader bgColor={Colors.transparent} />;
		}

		if (status === 'error') {
			return (
				<EmptyState
					icon="alert-circle-outline"
					message={Translations.mySubmissionErrorMessage}
					title={Translations.mySubmissionErrorTitle}
				/>
			);
		}

		if (status === 'not-found' || submission === null) {
			return (
				<EmptyState
					icon="ticket-outline"
					message={Translations.mySubmissionEmptyMessage}
					title={Translations.mySubmissionEmptyTitle}
				/>
			);
		}

		return (
			<View style={styles.successContainer} testID="my-submission-success">
				<View style={styles.ticket}>
					<Text style={styles.helper}>{Translations.mySubmissionHelper}</Text>
					<View style={[styles.qrBox, sizedStyles.qrBox]}>
						<SvgXml height={qrSize} width={qrSize} xml={submission.qr_svg} />
					</View>
					<Text style={styles.registrationLabel}>{Translations.mySubmissionRegistrationLabel}</Text>
					<Text style={styles.registrationNumber} testID="my-submission-registration-number">
						{submission.registration_number}
					</Text>
				</View>
				{submission.programme && submission.programme.length > 0 ? (
					<View style={[styles.ticket, styles.programme]} testID="my-submission-programme">
						<Text style={styles.registrationLabel}>{Translations.mySubmissionProgrammeTitle}</Text>
						{submission.programme.map((day, dayIndex) => (
							<View key={`${day.day}-${dayIndex}`} style={styles.programmeDay}>
								{day.day !== '' ? <Text style={styles.programmeDayTitle}>{day.day}</Text> : null}
								{day.lines.map((line, lineIndex) => (
									<Text key={`${line}-${lineIndex}`} style={styles.programmeLine}>
										{line}
									</Text>
								))}
							</View>
						))}
					</View>
				) : null}
			</View>
		);
	};

	return (
		<View style={styles.root}>
			<NavBar
				backBtn={true}
				bgImage={theme.header_bg_image}
				bgcolor={theme.header_bg_color}
				color={theme.header_text_color}
				editBtn={false}
				statusBarStyle={theme.statusbar_color}
				title={Translations.titleMySubmission}
			/>
			<Card backgroundColor={theme.app_bg_color} backgroundImage={theme.app_bg_image}>
				<ScrollView contentContainerStyle={styles.scrollContent}>{renderContent()}</ScrollView>
			</Card>
		</View>
	);
}

export default MySubmissionScreen;

const styles = StyleSheet.create({
	root: {
		flex: 1,
	},
	scrollContent: {
		flexGrow: 1,
	},
	successContainer: {
		flex: 1,
		alignItems: 'center',
		paddingHorizontal: Dimens.padding24,
		paddingVertical: Dimens.padding32,
	},
	// White panel so the QR (dark-on-light) and the number stay legible on any event theme background.
	ticket: {
		width: '100%',
		maxWidth: 360,
		alignItems: 'center',
		backgroundColor: Colors.white,
		borderRadius: 16,
		paddingVertical: Dimens.padding32,
		paddingHorizontal: Dimens.padding24,
	},
	helper: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize15,
		lineHeight: Dimens.lineHeight20,
		color: Colors.greyDark,
		textAlign: 'center',
		marginBottom: Dimens.margin24,
	},
	qrBox: {
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: Colors.white,
	},
	registrationLabel: {
		fontFamily: Fonts.montserratMedium,
		fontSize: Dimens.fontSize13,
		color: Colors.greyDark,
		textAlign: 'center',
		marginTop: Dimens.margin24,
		textTransform: 'uppercase',
		letterSpacing: 1,
	},
	programme: {
		marginTop: Dimens.margin24,
		paddingVertical: Dimens.padding24,
	},
	programmeDay: {
		alignSelf: 'stretch',
		marginTop: Dimens.margin16,
	},
	programmeDayTitle: {
		fontFamily: Fonts.montserratBold,
		fontSize: Dimens.fontSize15,
		color: Colors.greyTextDark,
		marginBottom: Dimens.margin6,
	},
	programmeLine: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize15,
		lineHeight: Dimens.lineHeight20,
		color: Colors.greyTextDark,
	},
	registrationNumber: {
		fontFamily: Fonts.montserratBold,
		fontSize: Dimens.fontSize24,
		color: Colors.greyTextDark,
		textAlign: 'center',
		marginTop: Dimens.margin6,
	},
});
